"use client";

import { useRef } from "react";
import NextImage from "next/image";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import {
  CountdownBox,
  EventNavbar,
  TicketCTA,
  candle1,
  candle2,
  candle3,
  candle4,
  gridTile,
  wave,
} from "@/components/pageComponents/event/EventShared";

import statue from "@/public/event/statue.png";

const ease = [0.25, 0.1, 0.25, 1] as const;
/** Entering motion shared by the stacked hero content (title, copy, CTA, countdown, mobile lists). */
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};
const enter = (delay: number) => ({ duration: 0.5, ease, delay });

/** Desktop scatter positions for the feature pills, by array index (array order = mobile order). */
const DESKTOP_PILL_SLOTS = [
  "left-[calc(50%+32.625rem)] top-[112rem]",
  "left-[calc(50%-62rem)] top-[118rem]",
  "left-[calc(50%-70rem)] top-[100rem] ",
  "left-[calc(50%+32rem)] top-[88rem]",
];

/** Dark hero: parallax backdrop, navbar, title + CTA + countdown, feature pills. */
export function EventHero({ data }: { data: PageEventSchema }) {
  const isMobile = useIsMobile();
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroSpring = { stiffness: 90, damping: 24, mass: 0.6 };
  const heroStatueY = useSpring(useTransform(heroProgress, [0, 1], [0, 60]), heroSpring);
  // Depth layers: candles (far) drift down slowly, pills (near) float up at two speeds.
  const heroCandlesY = useSpring(useTransform(heroProgress, [0, 1], [0, 30]), heroSpring);
  const heroPillsSlowY = useSpring(useTransform(heroProgress, [0, 1], [0, -35]), heroSpring);
  const heroPillsFastY = useSpring(useTransform(heroProgress, [0, 1], [0, -70]), heroSpring);

  const features = data.features ?? [];

  return (
    <section
      ref={heroRef}
      className="relative h-[172rem] w-full overflow-clip rounded-b-[1.25rem] bg-[#10322a] lg:h-[135rem]">
      {/* ── Decorative background layers — blur in together on load ──── */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, filter: "blur(8px)" }}
        animate={{ opacity: 1, filter: "blur(0px)" }}
        transition={{ duration: 0.7, ease }}
        className="pointer-events-none absolute inset-0">
        {/* Dotted grid texture */}
        <div
          className="absolute inset-0 opacity-[0.23]"
          style={{
            backgroundImage: `url(${gridTile.src})`,
            backgroundSize: "3.5rem 3.5rem",
            backgroundPosition: "top left",
          }}
        />

        {/* Glowing chart line sweeping across the page */}
        <div className="absolute top-[56.625rem] left-[-7.75rem] h-[156.25rem] w-[271.875rem]">
          <div className="absolute inset-[-47.12%_-27.07%]">
            <NextImage
              src={wave}
              alt=""
              className="block !h-full !w-full !max-w-none"
            />
          </div>
        </div>

        {/* Candlestick clusters (left/bottom + right/top) */}
        {["left-[-3.5rem] top-[50.875rem]", "right-[-8.875rem] top-[7.25rem]"].map((pos) => (
          <motion.div
            key={pos}
            style={isMobile === false ? { y: heroCandlesY } : undefined}
            className={cn("absolute hidden h-[96.75rem] w-[109rem] overflow-clip opacity-[0.13] lg:block", pos)}>
            <div className="absolute inset-[73.54%_156.63%_-0.03%_-108.49%] opacity-60 mix-blend-overlay">
              <NextImage
                src={candle1}
                alt=""
                className="absolute inset-0 !h-full !w-full !max-w-none"
              />
            </div>
            <div className="absolute inset-[49.19%_104.49%_24.32%_-56.34%] opacity-60 mix-blend-overlay">
              <NextImage
                src={candle2}
                alt=""
                className="absolute inset-0 !h-full !w-full !max-w-none"
              />
            </div>
            <div className="absolute inset-[24.35%_52.15%_49.15%_-4%] opacity-60 mix-blend-overlay">
              <NextImage
                src={candle3}
                alt=""
                className="absolute inset-0 !h-full !w-full !max-w-none"
              />
            </div>
            <div className="absolute inset-[0_0_73.51%_48.15%] opacity-60 mix-blend-overlay">
              <NextImage
                src={candle4}
                alt=""
                className="absolute inset-0 !h-full !w-full !max-w-none"
              />
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Statue — outer: scroll parallax; inner: rises in on load (nested so the two transforms don't fight) */}
      <motion.div
        style={isMobile === false ? { y: heroStatueY, x: "-50%" } : { x: "-50%" }}
        className="pointer-events-none absolute bottom-0 left-1/2 z-[5]">
        <motion.div
          initial={{ opacity: 0, y: 80, scale: 1.04 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, ease, delay: 0.05 }}
          style={{ transformOrigin: "50% 100%" }}>
          <NextImage
            src={statue}
            alt=""
            priority
            className="h-[42.75rem] w-[83.5rem] !max-w-none object-cover object-bottom lg:h-[64.75rem] lg:w-[132.875rem] lg:object-fill"
          />
        </motion.div>
      </motion.div>

      <EventNavbar
        data={data.navCta}
        textMobile={data.navCtaTextMobile}
        countdownTarget={data.countdownTarget}
      />

      {/* ── Hero content ─────────────────────────────────────────────── */}
      <div className="z-10 mx-auto flex w-[49.75rem] flex-col items-center gap-[3.75rem] pt-[20rem] lg:absolute lg:top-[calc(50%-28rem)] lg:left-1/2 lg:mx-0 lg:w-[139.5rem] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:gap-[4rem]">
        <div className="flex flex-col items-center justify-center">
          {(data.titleLine1 || data.titleLine2) && (
            <motion.h1
              {...fadeUp}
              transition={enter(0.15)}
              className="m-0 w-[39rem] text-center font-blacker text-[4.625rem] leading-[1.12] font-normal tracking-[-0.14rem] text-white lg:w-auto lg:text-[7rem] lg:tracking-[-0.21rem] [&_span]:font-blacker">
              {data.titleLine1 && <span className="block italic lg:text-[#cfffdb]">{data.titleLine1}</span>}
              {data.titleLine2 && <span className="block">{data.titleLine2}</span>}
            </motion.h1>
          )}

          {(data.descriptionStrong || data.descriptionMuted) && (
            <motion.p
              {...fadeUp}
              transition={enter(0.25)}
              className="mb-0 w-[min(47.625rem,100%)] px-0 text-center font-geist text-[2.25rem] leading-[1.6] lg:w-[77.75rem] lg:px-0">
              {data.descriptionStrong && <span className="font-medium text-white">{data.descriptionStrong} </span>}
              {data.descriptionMuted && <span className="font-normal text-white/70">{data.descriptionMuted}</span>}
            </motion.p>
          )}
        </div>

        <motion.div
          {...fadeUp}
          transition={enter(0.35)}
          className="mt-[2.125rem] flex w-full justify-center lg:mt-0">
          <TicketCTA
            data={data.cta}
            className="w-full lg:w-[47.625rem]"
          />
        </motion.div>
        <motion.div
          {...fadeUp}
          transition={enter(0.45)}
          className="w-[min(49.75rem,100%)] lg:w-auto">
          <CountdownBox
            label={data.countdownLabel}
            target={data.countdownTarget}
            className="gap-[0.9375rem] px-[2.5rem] pt-[2.5rem] lg:gap-[1.5rem] lg:px-[2.875rem] lg:pt-[2.875rem]"
          />
        </motion.div>
      </div>

      {/* ── Feature pills — desktop scatter ──────────────────────────── */}
      {/* Outer: position + scroll parallax. Inner: look + staggered entering (nested so the two transforms don't fight). */}
      {features.slice(0, DESKTOP_PILL_SLOTS.length).map((feature, i) => (
        <motion.div
          key={feature._key ?? i}
          style={isMobile === false ? { y: i % 2 ? heroPillsSlowY : heroPillsFastY } : undefined}
          className={cn("absolute hidden lg:block", i === 3 ? "z-[1]" : "z-10", DESKTOP_PILL_SLOTS[i])}>
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease, delay: 0.45 + i * 0.06 }}
            className="flex items-center gap-[1.5rem] rounded-[0.75rem] bg-[#151515]/[0.23] py-[1rem] pr-[2rem] pl-[1rem] backdrop-blur-[2rem]">
            <span className="flex h-[5.5rem] w-[5.5rem] shrink-0 items-center justify-center rounded-[0.5rem] bg-white">
              {feature.icon?.src && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={feature.icon.src}
                  alt=""
                  className="h-[3rem] w-[3rem]"
                />
              )}
            </span>
            <p className="mb-0 font-geist text-[2rem] leading-[1.35] font-normal whitespace-nowrap text-white opacity-80">
              {feature.text}
            </p>
          </motion.div>
        </motion.div>
      ))}

      {/* ── Feature pills — mobile stacked list ──────────────────────── */}
      {!!features.length && (
        <motion.div
          {...fadeUp}
          transition={enter(0.5)}
          className="relative z-10 mx-auto mt-[1.875rem] flex w-[min(49.75rem,100%)] flex-col gap-[1rem] lg:hidden">
          {features.map((feature, i) => (
            <div
              key={feature._key ?? i}
              className="flex w-full items-center gap-[1.5rem] overflow-clip rounded-[0.75rem] bg-white p-[0.5rem]">
              <span className="flex h-[4.875rem] w-[4.875rem] shrink-0 items-center justify-center rounded-[0.4375rem] bg-[#ffda74]">
                {feature.icon?.src && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={feature.icon.src}
                    alt=""
                    className="h-[2.66rem] w-[2.66rem] brightness-0"
                  />
                )}
              </span>
              <p className="mb-0 font-geist text-[2rem] leading-[1.35] font-normal whitespace-nowrap text-black opacity-70">
                {feature.text}
              </p>
            </div>
          ))}
        </motion.div>
      )}
      <motion.div
        {...fadeUp}
        transition={enter(0.6)}
        className="mx-auto mt-[4rem] w-[min(49.75rem,100%)] lg:hidden">
        <TicketCTA
          data={data.cta}
          className="w-full lg:w-[47.625rem]"
        />
      </motion.div>
    </section>
  );
}
